import { FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { JSX, Suspense } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/buttons/Button.tsx";
import CircleButton from "@/components/buttons/CircleButton";
import CreditCard from "@/components/cards/CreditCard/CreditCard.tsx";
import Spinner from "@/components/helpers/Spinner/Spinner.tsx";
import { IconCoinsStacked, IconPlus, IconSale } from "@/components/icons";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";
import BackButton from "@/components/staff/BackButton.tsx";
import CardsDrawer from "@/components/staff/payment/CardsDrawer.tsx";
import AddCardModal from "@/components/staff/payment/modal/AddCardModal/AddCardModal.tsx";

import { UserModel } from "@/api/user/user.types";
import { useGetCreditCards, useGetUser } from "@/hooks/api/user.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { cn } from "@/utils/cn.ts";
import { isMobile } from "@/utils/constatns";
import { currencyFormat } from "@/utils/currency";

const BalanceCard = ({ user }: { user: UserModel }) => {
  const { t } = useTranslation();

  return (
    <div
      className={"flex h-[16.25rem] flex-col justify-between rounded-2xl p-6 max-lg:p-4 max-md:h-[11.5rem]"}
      style={{ background: "linear-gradient(358deg, #F25001 -89.45%, #121212 98.06%), #FFF" }}
    >
      <div className={"flex justify-between gap-3 "}>
        <h2 className={"flex items-center gap-2 text-lg/normal max-md:text-sm/normal"}>
          <IconCoinsStacked className={"size-8 stroke-on-primary max-md:size-6"} />
          {t("balance")}
        </h2>
        <div className={"text-lg/normal"}>
          <p className={"text-end font-medium max-md:text-sm/normal"}>{user.id}</p>
          <p className={" text-right text-grey-1 max-sm:text-xs/normal 2xl:block "}>{t("Payment.account-splay")}</p>
        </div>
      </div>
      <p className={"text-[2rem]/normal font-medium max-lg:text-2xl/normal"}>
        {t("General.som", { value: currencyFormat(user.balance / 100) })}
      </p>
    </div>
  );
};

const CashbackCard = ({ cashback }: { cashback: number }) => {
  const { t } = useTranslation();

  return (
    <div
      className={
        "relative flex h-[16.25rem] flex-col justify-between overflow-hidden rounded-2xl p-6 max-lg:p-4 max-md:h-[11.5rem]"
      }
      style={{ background: "linear-gradient(303deg, #F25001 -151.41%, #222 78.21%), #FFF" }}
    >
      <div className={"flex justify-between"}>
        <h2 className={"flex items-center gap-2 text-lg/normal max-md:text-sm/normal"}>
          <IconSale className={"size-8 stroke-on-primary max-md:size-6"} />
          {t("Payment.cashback")}
        </h2>
      </div>
      <p className={"text-[2rem]/normal font-medium max-lg:text-2xl/normal"}>
        {t("General.som", { value: currencyFormat(cashback) })}
      </p>
      <IconSale
        className={
          "absolute -bottom-7 -right-3 size-[8.75rem] stroke-orange-1 opacity-20 max-lg:-bottom-5 max-lg:-right-2 max-lg:size-24"
        }
      />
    </div>
  );
};

const CreditCards = () => {
  const { data: creditCards } = useGetCreditCards();
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "h-[16.25rem] rounded-2xl bg-surface-container px-6 py-4",
        "max-md:h-[11.5rem] max-md:p-4",
        "flex flex-col max-2xl:col-span-2 max-xl:col-span-1 max-md:col-span-1",
      )}
    >
      <div className={"flex items-center justify-between"}>
        <h2 className={"text-xl/7 font-medium max-md:text-base/5"}>{t("Payment.card", { count: 2 })}</h2>
        <Suspense fallback={<Spinner className={"size-5 max-md:size-4"} />}>
          <CardsDrawer />
        </Suspense>
      </div>
      <div className={"relative mt-4 flex-1 max-md:mt-2"}>
        {creditCards.length == 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex flex-col items-center">
              <p className="mb-5 text-center text-xl">{t("cards.empty")}</p>
              <AddCardModal className="max-w-52" />
            </div>
          </div>
        ) : (
          <></>
        )}

        {creditCards[0] && (
          <CreditCard actions={false} creditCard={creditCards[0]} className={"h-[11.5rem]  max-md:h-[8.125rem]"} />
        )}

        {creditCards[1] && (
          <CreditCard
            actions={false}
            className={
              "absolute right-0 top-0 aspect-[240/184] h-[11.5rem] max-w-60 max-2xl:max-w-[60%] max-md:h-[8.125rem]"
            }
            creditCard={creditCards[1]}
          />
        )}
      </div>
    </div>
  );
};

const PaymentBalance = (): JSX.Element => {
  const { t } = useTranslation();

  const { data, isLoading } = useGetUser();
  const { isLoading: CreditCardsLoading } = useGetCreditCards();
  const { openModal } = useModalRouter();

  return (
    <section className={"staff-section-container pb-0"}>
      <div>
        <BackButton />
        <div className={"flex items-center justify-between max-lg:mt-8 max-sm:mt-5"}>
          <h1 className={"staff-section-title"}>{t("Payment.balance")}</h1>
          <CircleButton
            className="flex size-[2.5rem] items-center justify-center !bg-primary p-2 sm:hidden"
            icon={<IconPlus className="stroke-on-primary" />}
            buttonType={"entrance"}
            onClick={() => openModal([MODAL_ROUTES.TOP_UP, MODAL_ROUTES.TOP_UP_AMOUNT])}
          ></CircleButton>
          <Button
            onClick={() => openModal([MODAL_ROUTES.TOP_UP, MODAL_ROUTES.TOP_UP_AMOUNT])}
            size={"medium"}
            aria-label={t("Payment.top-up")}
            className={"w-fit max-sm:hidden"}
          >
            {t("Payment.top-up")}
          </Button>
        </div>

        <div className="mt-8 max-w-full max-sm:mt-4">
          <Swiper
            slidesPerView={3}
            slidesPerGroup={1}
            spaceBetween={8}
            breakpoints={{
              "0": { slidesPerView: 1.1 },
              "600": { slidesPerView: 2 },
              "1200": { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 16 },
            }}
            modules={[Navigation, FreeMode]}
            navigation={{
              prevEl: `.js-balance-section-prev`,
              nextEl: `.js-balance-section-next`,
            }}
            followFinger={isMobile}
            mousewheel={{
              forceToAxis: true,
            }}
            className="max-lg:overflow-visible"
            speed={500}
          >
            <SwiperSlide key={1}>
              {isLoading ? (
                <div className="animate-pulse rounded-[1.25rem] bg-surface-container"></div>
              ) : (
                <BalanceCard user={data} />
              )}
            </SwiperSlide>
            <SwiperSlide key={2}>
              {isLoading ? (
                <div className="animate-pulse rounded-[1.25rem] bg-surface-container"></div>
              ) : (
                <CashbackCard cashback={data.cashback_balance} />
              )}
            </SwiperSlide>
            <SwiperSlide key={3}>
              {CreditCardsLoading ? (
                <div className="animate-pulse rounded-[1.25rem] bg-surface-container"></div>
              ) : (
                <CreditCards />
              )}
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default PaymentBalance;
